// @file Helper for Whiteboard (tools, colors, etc)
import { trackEvent } from '@@/bits/analytics'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { setSearchParam } from '@@/bits/location'
import { tailwindFlattenedColors } from '@@/bits/tailwind'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { handleGroupLinking } from '@@/bits/whiteboard_linking'
import type {
  TldrawColorVariantType,
  TldrawDefaultColorStyleColor,
  ToolTailwindColorType,
} from '@@/bits/whiteboard_tools'
import { TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR } from '@@/bits/whiteboard_tools'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { processedUrl } from '@padlet/vivaldi-client'
import type { IndexKey, JsonObject, TLGroupShape, TLPage, TLPageId, TLRecord, TLShape } from 'tldraw'
import { DefaultColorThemePalette, getIndexAbove, getIndexBelow, getIndexBetween, PageRecordType, TAB_ID } from 'tldraw'

const CANVAS_HEIGHT = 1080
const CANVAS_WIDTH = 1920
const MAX_FRAME_LIMIT = 100

function getCanvasDimensions(): { height: number; width: number } {
  const editor = getEditorObject()
  if (editor == null) {
    return {
      height: CANVAS_HEIGHT,
      width: CANVAS_WIDTH,
    }
  }
  const { height, width } = editor.getViewportPageBounds()
  return {
    height,
    width,
  }
}

function getTailwindBgColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'bg-neon-pink'
    case 'neon-yellow':
      return 'bg-neon-yellow'
    case 'neon-orange':
      return 'bg-neon-orange'
    case 'neon-green':
      return 'bg-neon-green'
    case 'neon-blue':
      return 'bg-neon-blue'
    case 'grey-200':
      return 'bg-grey-200'
    case 'scarlet-500':
      return 'bg-scarlet-500'
    case 'canary-500':
      return 'bg-canary-500'
    case 'park-500':
      return 'bg-park-500'
    case 'oceanic-500':
      return 'bg-oceanic-500'
    case 'oceanic-400':
      return 'bg-oceanic-400'
    case 'grape-200':
      return 'bg-grape-200'
    case 'oceanic-200':
      return 'bg-oceanic-200'
    case 'canary-200':
      return 'bg-canary-200'
    case 'park-200':
      return 'bg-park-200'
    case 'scarlet-200':
      return 'bg-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'bg-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'bg-grape-500'
    case 'tangerine-600':
      return 'bg-tangerine-600'
    case 'canary-600':
      return 'bg-canary-600'
    case 'park-600':
      return 'bg-park-600'
    case 'teal-500':
      return 'bg-teal-500'
    case 'dark-text-100':
      return 'bg-dark-text-100'
    case 'light-text-100':
      return 'bg-light-text-100'
    default:
      return null
  }
}

function getTailwindColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'text-neon-pink'
    case 'neon-yellow':
      return 'text-neon-yellow'
    case 'neon-orange':
      return 'text-neon-orange'
    case 'neon-green':
      return 'text-neon-green'
    case 'neon-blue':
      return 'text-neon-blue'
    case 'grey-200':
      return 'text-grey-200'
    case 'scarlet-500':
      return 'text-scarlet-500'
    case 'canary-500':
      return 'text-canary-500'
    case 'park-500':
      return 'text-park-500'
    case 'oceanic-500':
      return 'text-oceanic-500'
    case 'oceanic-400':
      return 'text-oceanic-400'
    case 'grape-200':
      return 'text-grape-200'
    case 'oceanic-200':
      return 'text-oceanic-200'
    case 'canary-200':
      return 'text-canary-200'
    case 'park-200':
      return 'text-park-200'
    case 'scarlet-200':
      return 'text-scarlet-200'
    case 'whiteboard-shape-border-grape':
      return 'text-whiteboard-shape-border-grape'
    case 'grape-500':
      return 'text-grape-500'
    case 'tangerine-600':
      return 'text-tangerine-600'
    case 'canary-600':
      return 'text-canary-600'
    case 'park-600':
      return 'text-park-600'
    case 'teal-500':
      return 'text-teal-500'
    case 'teal-200':
      return 'text-teal-200'
    case 'teal-100':
      return 'text-teal-100'
    case 'scarlet-100':
      return 'text-scarlet-100'
    case 'canary-100':
      return 'text-canary-100'
    case 'whiteboard-shape-fill-park':
      return 'text-whiteboard-shape-fill-park'
    case 'oceanic-100':
      return 'text-oceanic-100'
    case 'grey-100':
      return 'text-grey-100'
    case 'grey-400':
      return 'text-grey-400'
    case 'grey-300':
      return 'text-grey-300'
    case 'dark-text-100':
      return 'text-dark-text-100'
    case 'light-text-100':
      return 'text-light-text-100'
    case 'light-ui-300':
      return 'text-light-ui-300'
    default:
      return null
  }
}

function getTailwindRingColorClass(toolId: string): string | null {
  const color = toolId

  switch (color) {
    case 'neon-pink':
      return 'ring-neon-pink'
    case 'neon-yellow':
      return 'ring-neon-yellow'
    case 'neon-orange':
      return 'ring-neon-orange'
    case 'neon-green':
      return 'ring-neon-green'
    case 'neon-blue':
      return 'ring-neon-blue'
    case 'grey-200':
      return 'ring-grey-200'
    case 'scarlet-500':
      return 'ring-scarlet-500'
    case 'canary-500':
      return 'ring-canary-500'
    case 'park-500':
      return 'ring-park-500'
    case 'oceanic-500':
      return 'ring-oceanic-500'
    case 'grape-200':
      return 'ring-grape-200'
    case 'oceanic-200':
      return 'ring-oceanic-200'
    case 'canary-200':
      return 'ring-canary-200'
    case 'park-200':
      return 'ring-park-200'
    case 'scarlet-200':
      return 'ring-scarlet-200'
    case 'grape-600':
      return 'ring-grape-600'
    case 'tangerine-600':
      return 'ring-tangerine-600'
    case 'canary-600':
      return 'ring-canary-600'
    case 'park-600':
      return 'ring-park-600'
    case 'dark-text-100':
      return 'ring-dark-text-100'
    case 'light-text-100':
      return 'ring-light-text-100'
    default:
      return null
  }
}

// Map extra colors to the closest tldraw default color style color
// This is for Jamboard shapes color mapping
const EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<string, TldrawDefaultColorStyleColor>>
> = {
  highlight: {
    'grey-200': 'black', // Jamboard shape border color
    'canary-500': 'light-red',
  },
  solid: {},
  note: {},
  semi: {
    'oceanic-300': 'blue', // Jamboard shape fill color
    'grape-100': 'violet',
    'park-100': 'green',
    // 'grey-200': 'white',
  },
}

type SolidColor = string
interface HighlightColor {
  srgb: string
  p3: string
}
interface NoteColor {
  fill: string
  text: string
}
function changeTldrawDefaultColorStyleColor(
  color: string,
  variant: 'solid' | 'highlight' | 'note' | 'semi',
  toolColor: ToolTailwindColorType,
): void {
  // For darkmode, we are setting to the same color as light mode since we're ignoring dark mode for now in v1
  // TODO: Add dark mode colorset
  for (const mode of ['lightMode', 'darkMode'] as const) {
    let targetColor: SolidColor | HighlightColor | NoteColor = '#000000'

    if (variant === 'solid' || variant === 'semi') {
      targetColor = tailwindFlattenedColors[toolColor]
    }
    if (variant === 'highlight') {
      targetColor = {
        srgb: tailwindFlattenedColors[toolColor],
        p3: tailwindFlattenedColors[toolColor],
      }
    }
    if (variant === 'note') {
      targetColor = {
        fill: tailwindFlattenedColors[toolColor],
        text: '#000000',
      }
    }
    DefaultColorThemePalette[mode][color][variant] = targetColor
  }
}

function changeAllTldrawDefaultColorStyleColor(): void {
  const variants = Object.keys(TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR) as TldrawColorVariantType[]
  variants.forEach((variant) => {
    const colors = TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
  const variantsForExtraColors = Object.keys(
    EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR,
  ) as TldrawColorVariantType[]
  variantsForExtraColors.forEach((variant) => {
    const colors = EXTRA_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[variant]
    const tailwindColors = Object.keys(colors) as ToolTailwindColorType[]
    tailwindColors.forEach((tailwindColor) => {
      const tldrawColor = colors[tailwindColor]
      if (tldrawColor == null) {
        return
      }
      changeTldrawDefaultColorStyleColor(tldrawColor, variant as 'solid' | 'highlight' | 'note' | 'semi', tailwindColor)
    })
  })
}

// Actions

enum ActionType {
  Undo = 'Undo',
  Redo = 'Redo',
  ToggleSnapOn = 'ToggleSnapOn',
  ToggleSnapOff = 'ToggleSnapOff',
  SendToFront = 'SendToFront',
  SendToBack = 'SendToBack',
  Link = 'Link',
  Cut = 'Cut',
  Copy = 'Copy',
  Paste = 'Paste',
  Duplicate = 'Duplicate',
  ToggleFreeze = 'ToggleFreeze',
  Delete = 'Delete',
  SelectAll = 'SelectAll',
  GoToPreviousPage = 'GoToPreviousPage',
  GoToNextPage = 'GoToNextPage',
  Group = 'Group',
  Ungroup = 'Ungroup',
}

const actionShortcuts = [
  {
    actionType: ActionType.Undo,
    kbd: '$mod+z',
  },
  {
    actionType: ActionType.Redo,
    kbd: '$mod+Shift+z',
  },
  {
    actionType: ActionType.SendToFront,
    kbd: ']',
  },
  {
    actionType: ActionType.SendToBack,
    kbd: '[',
  },
  {
    actionType: ActionType.Group,
    kbd: '$mod+g',
  },
  {
    actionType: ActionType.Ungroup,
    kbd: '$mod+Shift+g',
  },
  {
    actionType: ActionType.Link,
    kbd: 'Shift+l',
  },
  {
    actionType: ActionType.Cut,
    kbd: '$mod+x',
  },
  {
    actionType: ActionType.Copy,
    kbd: '$mod+c',
  },
  {
    actionType: ActionType.Paste,
    kbd: '$mod+v',
  },
  {
    actionType: ActionType.Duplicate,
    kbd: '$mod+d',
  },
  {
    actionType: ActionType.ToggleFreeze,
    kbd: 'Shift+f',
  },
  {
    actionType: ActionType.Delete,
    kbd: 'Backspace',
  },
  {
    actionType: ActionType.SelectAll,
    kbd: '$mod+a',
  },
]

// Adapted from: https://github.com/tldraw/tldraw/blob/v2.1.4/packages/tldraw/src/lib/ui/context/actions.tsx
// Previously tried to set the output of useActions on window but the behavior was very buggy
const handleAction = (actionType: ActionType, checkCanEdit: (shape: TLShape) => boolean): void => {
  const tldrawEditor = getEditorObject()
  const selectedShapes = tldrawEditor?.getSelectedShapes() ?? []
  // We only allow actions on the least permissive of the selected objects
  const areAllSelectedShapesEditable = selectedShapes.every(checkCanEdit)

  switch (actionType) {
    case ActionType.Undo: {
      trackEvent('Whiteboard', 'Undo action')
      tldrawEditor?.undo()
      break
    }
    case ActionType.Redo: {
      trackEvent('Whiteboard', 'Redo action')
      tldrawEditor?.redo()
      break
    }
    case ActionType.ToggleSnapOn: {
      trackEvent('Whiteboard', 'Toggle snap on')
      tldrawEditor?.user.updateUserPreferences({ isSnapMode: true })
      break
    }
    case ActionType.ToggleSnapOff: {
      trackEvent('Whiteboard', 'Toggle snap off')
      tldrawEditor?.user.updateUserPreferences({ isSnapMode: false })
      break
    }
    case ActionType.SendToFront: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Send to front')
      tldrawEditor?.markHistoryStoppingPoint('bring to front')
      tldrawEditor?.bringToFront(selectedShapes)
      break
    }
    case ActionType.SendToBack: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Send to back')
      tldrawEditor?.markHistoryStoppingPoint('send to back')
      tldrawEditor?.sendToBack(selectedShapes)
      break
    }
    case ActionType.Group: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Group objects')
      const groupShapesMark = tldrawEditor?.markHistoryStoppingPoint('group shapes')
      if (groupShapesMark == null) return
      handleGroupLinking(selectedShapes, groupShapesMark)
      tldrawEditor?.groupShapes(selectedShapes)
      tldrawEditor?.squashToMark(groupShapesMark)
      break
    }
    case ActionType.Ungroup: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Ungroup objects')
      tldrawEditor?.ungroupShapes(selectedShapes)
      break
    }
    case ActionType.Cut: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Cut objects')
      tldrawEditor?.markHistoryStoppingPoint('cut')
      window.tldrawMenuClipboardEvents?.cut('unknown')
      break
    }
    case ActionType.Copy: {
      trackEvent('Whiteboard', 'Copy objects')
      tldrawEditor?.markHistoryStoppingPoint('copy')
      window.tldrawMenuClipboardEvents?.copy('unknown')
      break
    }
    case ActionType.Paste: {
      trackEvent('Whiteboard', 'Paste objects')
      void navigator.clipboard?.read().then((clipboardItems) => {
        void window.tldrawMenuClipboardEvents?.paste(clipboardItems, 'unknown', tldrawEditor?.inputs.currentPagePoint)
      })
      break
    }
    case ActionType.Duplicate: {
      trackEvent('Whiteboard', 'Duplicate objects')
      tldrawEditor?.markHistoryStoppingPoint('duplicate shapes')
      tldrawEditor?.duplicateShapes(selectedShapes, { x: 20, y: 20 })
      break
    }
    case ActionType.ToggleFreeze: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Toggle freeze')
      tldrawEditor?.markHistoryStoppingPoint('toggle freeze')
      tldrawEditor?.toggleLock(selectedShapes)
      break
    }
    case ActionType.Delete: {
      if (!areAllSelectedShapesEditable) {
        return
      }
      trackEvent('Whiteboard', 'Delete objects')
      tldrawEditor?.markHistoryStoppingPoint('delete')
      tldrawEditor?.deleteShapes(selectedShapes)
      break
    }
    case ActionType.SelectAll: {
      trackEvent('Whiteboard', 'Select all')
      if (isAppUsing('sandboxRetainActiveTool')) {
        useWhiteboardStore().selectTool('select')
        tldrawEditor?.setCurrentTool('select')
      }
      tldrawEditor?.markHistoryStoppingPoint('select all')
      tldrawEditor?.selectAll()
      break
    }
    case ActionType.GoToPreviousPage: {
      trackEvent('Whiteboard', 'Go to previous page')
      const previousPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index + 1]?.id === tldrawEditor?.getCurrentPageId())
      if (previousPage == null) return
      switchFrame(previousPage.id)
      break
    }
    case ActionType.GoToNextPage: {
      trackEvent('Whiteboard', 'Go to next page')
      const nextPage = tldrawEditor
        ?.getPages()
        .find((_page, index, pages) => pages[index - 1]?.id === tldrawEditor?.getCurrentPageId())
      if (nextPage == null) return
      switchFrame(nextPage.id)
      break
    }
    default:
      break
  }
}

// Frames

const FRAME_ID_QUERY_PARAM = 'frame_id'

interface PageMeta extends JsonObject {
  backgroundType?: BackgroundType
  framePermissionOverride?: FramePermissionOverride
  customBackgroundUrl?: string

  // Intentionally not include `createdAt` and `createdBy`
  // since we don't know who made which page now. We can
  // only know if we track from the beginning.
  lastModifiedBy?: number
  lastModifiedAt?: number
}

/**
 * Determine the new index key based on surrounding pages.
 */
const getNewIndexKey = (pageBefore: TLPage | null, pageAfter: TLPage | null): IndexKey => {
  if (pageBefore != null && pageAfter != null) {
    return getIndexBetween(pageBefore.index, pageAfter.index)
  }
  if (pageBefore != null) {
    return getIndexAbove(pageBefore.index)
  }
  if (pageAfter != null) {
    return getIndexBelow(pageAfter.index)
  }

  throw new Error('Unable to determine new index key; both pageBefore and pageAfter are null')
}

/**
 * Calculate a new index key for moving a frame from one position to another.
 *
 * The fromIndex is used to determine whether the generated index key comes before or after
 * the index key that currently occupies the toIndex.
 *
 * Example:
 * - If moving a frame from index 3 to index 2, generate an IndexKey that is between index 1 and 2.
 * - If moving a frame from index 1 to index 2, generate an IndexKey that is between index 2 and 3.
 */
const calculateIndexKeyForMoveFrame = (fromIndex: number, toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const isMovingToLowerIndex = fromIndex > toIndex

  const pageBefore = isMovingToLowerIndex ? pages[toIndex - 1] : pages[toIndex]
  const pageAfter = isMovingToLowerIndex ? pages[toIndex] : pages[toIndex + 1]

  return getNewIndexKey(pageBefore, pageAfter)
}

/**
 * Calculate a new index key for creating a new frame at a specified position (toIndex).
 *
 * Example:
 * - If inserting a new frame at index 2, generate an IndexKey that is between index 1 and 2.
 */
const calculateIndexKeyForNewFrame = (toIndex: number): IndexKey | null => {
  const editor = getEditorObject()
  const pages = editor?.getPages()
  if (pages == null) {
    return null
  }

  const pageBefore = pages[toIndex - 1]
  const pageAfter = pages[toIndex]

  return getNewIndexKey(pageBefore, pageAfter)
}

const moveFrame = (frameId: TLPageId, fromIndex: number, toIndex: number): void => {
  const toIndexKey = calculateIndexKeyForMoveFrame(fromIndex, toIndex)
  if (toIndexKey == null) {
    throw new Error('toIndexKey not found')
  }

  const editor = getEditorObject()
  if (editor == null) return

  editor.markHistoryStoppingPoint('moving page')
  editor.updatePage({
    id: frameId,
    index: toIndexKey,
  })
}

function addFrame(destinationIndex: number): void {
  const editor = getEditorObject()
  if (editor == null) return

  const newPageIndexKey = calculateIndexKeyForNewFrame(destinationIndex)
  if (newPageIndexKey == null) {
    return
  }

  const newPageId = PageRecordType.createId()
  editor.createPage({ name: 'Page', index: newPageIndexKey, id: newPageId })
  editor.setCurrentPage(newPageId)
}

function deleteFrame(frameId: TLPageId): void {
  const editor = getEditorObject()
  if (editor == null) return
  const shapeIdsForPage = editor.getPageShapeIds(frameId)

  editor.markHistoryStoppingPoint('delete page and shapes')
  // We need to batch because deletePage does not automatically delete shapes
  // so they are orphaned in the store and it causes inconsistent undo behavior
  // (probably due to garbage collection)
  editor.run(
    () => {
      editor.deleteShapes([...shapeIdsForPage])
      editor.deletePage(frameId)
    },
    { history: 'record' },
  )
}

function switchFrame(frameId: TLPageId): void {
  const editor = getEditorObject()
  if (editor == null) return
  editor.setCurrentPage(frameId)
  setSearchParam(FRAME_ID_QUERY_PARAM, frameId)
}

// Backgrounds

enum BackgroundType {
  // Standard
  Plain = 'Plain',
  DotGrid = 'DotGrid',
  Grid = 'Grid',
  Ruled = 'Ruled',
  Frosted = 'Frosted',

  // Template
  VennDiagram = 'VennDiagram',
  WeekdaysCalendar = 'WeekdaysCalendar',
  WeeklyCalendar = 'WeeklyCalendar',
  MonthlyCalendar = 'MonthlyCalendar',
  Timeline = 'Timeline',
  OneHundredChart = 'OneHundredChart',
  TwoColumns = 'TwoColumns',
  ThreeColumns = 'ThreeColumns',
  FourColumns = 'FourColumns',
  FiveColumns = 'FiveColumns',
  FourCorners = 'FourCorners',
  Blueprint = 'Blueprint',
  Chalkboard = 'Chalkboard',
  Custom = 'Custom',
}

const STANDARD_BACKGROUND_TYPES = [
  BackgroundType.Plain,
  BackgroundType.Ruled,
  BackgroundType.DotGrid,
  BackgroundType.Grid,
  BackgroundType.Frosted,
]
const TEMPLATE_BACKGROUND_TYPES = [
  BackgroundType.VennDiagram,
  BackgroundType.WeekdaysCalendar,
  BackgroundType.WeeklyCalendar,
  BackgroundType.MonthlyCalendar,
  BackgroundType.Timeline,
  BackgroundType.OneHundredChart,
  BackgroundType.TwoColumns,
  BackgroundType.ThreeColumns,
  BackgroundType.FourColumns,
  BackgroundType.FiveColumns,
  BackgroundType.FourCorners,
]

// Background svgs are uploaded to this bucket:
// https://console.cloud.google.com/storage/browser/padlet-static-production/whiteboard/frame-backgrounds?project=logical-handler-867
const BACKGROUND_TYPE_TO_URL_MAPPING: { [key in BackgroundType]: string } = {
  // 128x128

  [BackgroundType.Plain]: 'https://padlet.net/whiteboard/frame-backgrounds/plain_background.svg',
  [BackgroundType.Grid]: 'https://padlet.net/whiteboard/frame-backgrounds/grid_background.svg',
  [BackgroundType.Ruled]: 'https://padlet.net/whiteboard/frame-backgrounds/ruled_background.svg',
  [BackgroundType.Frosted]: 'https://padlet.net/whiteboard/frame-backgrounds/frosted_glass.svg',

  // 1920x1080

  // For DotGrid, we tried to use 128x128 but the browser makes it slightly deformed at the edges when it repeats
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background.svg',

  [BackgroundType.VennDiagram]: 'https://padlet.net/whiteboard/frame-backgrounds/venn_diagram.svg',
  [BackgroundType.WeekdaysCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekdays_calendar.svg',
  [BackgroundType.WeeklyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/weekly_calendar.svg',
  [BackgroundType.MonthlyCalendar]: 'https://padlet.net/whiteboard/frame-backgrounds/monthly_calendar.svg',
  [BackgroundType.Timeline]: 'https://padlet.net/whiteboard/frame-backgrounds/timeline.svg',
  [BackgroundType.OneHundredChart]: 'https://padlet.net/whiteboard/frame-backgrounds/one_hundred_chart.svg',
  [BackgroundType.TwoColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/two_columns.svg',
  [BackgroundType.ThreeColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/three_columns.svg',
  [BackgroundType.FourColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/four_columns.svg',
  [BackgroundType.FiveColumns]: 'https://padlet.net/whiteboard/frame-backgrounds/five_columns.svg',
  [BackgroundType.FourCorners]: 'https://padlet.net/whiteboard/frame-backgrounds/four_corners.svg',
  [BackgroundType.Custom]: '',

  // From Jamboard
  [BackgroundType.Blueprint]: 'https://padlet.net/whiteboard/frame-backgrounds/blueprint.svg',
  [BackgroundType.Chalkboard]: 'https://padlet.net/whiteboard/frame-backgrounds/chalkboard.svg',
}

const BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING: { [key in BackgroundType]: string } = {
  ...BACKGROUND_TYPE_TO_URL_MAPPING,
  // We use a separate (smaller) image for the dot grid preview otherwise the dots will not be visible
  [BackgroundType.DotGrid]: 'https://padlet.net/whiteboard/frame-backgrounds/dot_background_preview.svg',
  // We use a custom preview for the frosted glass background.
  [BackgroundType.Frosted]: 'https://padlet.net/whiteboard/frame-backgrounds/frosted_glass_preview.png',
}

// Permissions

enum FramePermissionOverride {
  None = 'None',
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
}

// We don't need a No access permission level because the visitor gets blocked at the route level instead
// I.e. they do not even load surface
enum PermissionLevel {
  Reader = 'Reader',
  Writer = 'Writer',
  Editor = 'Editor',
  Manager = 'Manager',
  Administrator = 'Administrator',
  Owner = 'Owner',
}

// Refer to table for breakdown of what each permission can do:
// https://whimsical.com/permissions-technical-representation-72rGSxT28JDQhMMGGEtEo6
interface Permissions {
  canRead: boolean
  canWrite: boolean
  canEdit: boolean
  canManage: boolean
  canAdminister: boolean
  canTrash: boolean
}

const FRAME_PERMISSION_OVERRIDE_MAPPING: { [key in FramePermissionOverride]: PermissionLevel | null } = {
  [FramePermissionOverride.None]: null,
  [FramePermissionOverride.Reader]: PermissionLevel.Reader,
  [FramePermissionOverride.Writer]: PermissionLevel.Writer,
  [FramePermissionOverride.Editor]: PermissionLevel.Editor,
}

const PERMISSIONS_MAPPING: { [key in PermissionLevel]: Permissions } = {
  [PermissionLevel.Reader]: {
    canRead: true,
    canWrite: false,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Writer]: {
    canRead: true,
    canWrite: true,
    canEdit: false,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  // Editor permission is used for frame-level overrides
  // Allows editing frame content, but restricts frame management
  // Users can modify existing elements but cannot add, remove, or reorder frames
  [PermissionLevel.Editor]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: false,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Manager]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: false,
    canTrash: false,
  },
  [PermissionLevel.Administrator]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: false,
  },
  [PermissionLevel.Owner]: {
    canRead: true,
    canWrite: true,
    canEdit: true,
    canManage: true,
    canAdminister: true,
    canTrash: true,
  },
}

const setBackgroundType = (frameId: TLPageId, backgroundType: BackgroundType): void => {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const selectedFrame = tldrawEditor.getPage(frameId)
  if (selectedFrame != null) {
    tldrawEditor.updatePage({
      ...selectedFrame,
      meta: {
        ...selectedFrame.meta,
        backgroundType,
      },
    })
  }
}

const setCustomBackgroundUrl = (frameId: TLPageId, url: string): void => {
  const tldrawEditor = getEditorObject()
  if (tldrawEditor == null) return
  const selectedFrame = tldrawEditor.getPage(frameId)
  const processedBackgroundUrl = processedUrl(url, { width: CANVAS_WIDTH, height: CANVAS_HEIGHT })
  if (selectedFrame != null) {
    tldrawEditor.updatePage({
      ...selectedFrame,
      meta: {
        ...selectedFrame.meta,
        customBackgroundUrl: processedBackgroundUrl,
      },
    })
  }
}

function changeFrameBackground(
  frameId: TLPageId,
  backgroundType: BackgroundType,
  customBackgroundUrl?: string | null,
): void {
  setBackgroundType(frameId, backgroundType)
  if (backgroundType === BackgroundType.Custom && customBackgroundUrl != null) {
    setCustomBackgroundUrl(frameId, customBackgroundUrl)
  }
}

const getFramePermissionOverride = (frameId: TLPageId): FramePermissionOverride => {
  const editor = getEditorObject()
  const frame = editor?.getPage(frameId)
  const frameMeta: PageMeta | undefined = frame?.meta
  return frameMeta?.framePermissionOverride ?? FramePermissionOverride.None
}

const getFramePermissions = (frameId: TLPageId): Permissions | null => {
  const framePermissionOverride = getFramePermissionOverride(frameId)
  const permissionLevel = FRAME_PERMISSION_OVERRIDE_MAPPING[framePermissionOverride]
  return permissionLevel != null ? PERMISSIONS_MAPPING[permissionLevel] : null
}

const isShapeApproved = (record: TLRecord | null | undefined): boolean => {
  if (record == null) {
    return true
  }
  // We don't moderate non-shape records
  if (record.typeName !== 'shape') {
    return true
  }

  return typeof record?.meta.approved === 'boolean' ? record.meta.approved : true
}

const SHAPES_SUBJECTED_TO_AUTO_MODERATION = ['text', 'geo', 'note', 'padlet-attachment-shape']
const SHAPES_SUBJECTED_TO_TEXT_MODERATION = ['text', 'geo', 'note', 'padlet-speech-bubble']

const isShapeSubjectedToAutoModeration = (shape: TLShape): boolean => {
  return SHAPES_SUBJECTED_TO_AUTO_MODERATION.includes(shape.type)
}

const isShapeSubjectedToTextModeration = (shape: TLShape): boolean => {
  return SHAPES_SUBJECTED_TO_TEXT_MODERATION.includes(shape.type)
}

// This is `false` only for shapes awaiting moderation result
const doesShapeHaveAutoModerationResult = (record: TLRecord | null | undefined): boolean => {
  const submittedForModerationAt = record?.meta?.submittedForModerationAt
  // Cases where shape will not have a timestamp
  // 1. Shape was never submitted for moderation (Not part of auto moderation or created before auto moderation was enabled)
  // 2. Auto moderation results have been received. We delete the timestamp.
  // If there is no timestamp, refer to isShapeApproved to render the shape.
  if (submittedForModerationAt == null) {
    return true
  }

  const MODERATION_TIMEOUT = 5000 // just 5 seconds now for testing
  if (typeof submittedForModerationAt === 'number' && Date.now() - submittedForModerationAt > MODERATION_TIMEOUT) {
    // Default to manual moderation after timeout
    return true
  }

  return false
}

const approveShape = (shape: TLShape): void => {
  const editor = getEditorObject()
  editor?.updateShape({
    id: shape.id,
    type: shape.type,
    meta: {
      approved: true,
    },
  })
  editor?.selectNone()
}

// #region Embeds
type EmbedSize = 'small' | 'medium' | 'large'
interface EmbedSizeOption {
  title: string
  width: number
  height: number
  key: EmbedSize
}

// 16:9 aspect ratio sizes
const embedSizeOptions: EmbedSizeOption[] = [
  {
    title: __('Small'),
    width: 480,
    height: 320,
    key: 'small',
  },
  {
    title: __('Medium'),
    width: 720,
    height: 480,
    key: 'medium',
  },
  {
    title: __('Large'),
    width: 1080,
    height: 720,
    key: 'large',
  },
]
// #endregion

// #region Attachment Shapes
const isUploadingAttachmentShape = (record: TLRecord | null | undefined): boolean => {
  if (record == null || record.typeName !== 'shape') {
    return false
  }

  return typeof record.meta.isUploaded === 'boolean' && !record.meta.isUploaded
}
const isShapeCreatedInCurrentTab = (record: TLRecord | null | undefined): boolean => {
  if (record == null || record.typeName !== 'shape') {
    return false
  }
  // `TAB_ID` is stored in sessionStorage, i.e. it is unique to each tab
  return record.meta.tabId === TAB_ID
}
// #endregion

const deleteShapeMetadata = (shape: TLShape, keysToRemove: string[]): void => {
  const editor = getEditorObject()
  if (editor == null) return
  const metadata = { ...shape.meta }

  keysToRemove.forEach((key) => {
    delete metadata[key]
  })
  // Tldraw doesn't completely replace meta when updateShape is used, it combines it with the existing meta
  // We need to update the store directly to completely remove meta keys
  editor.store.put([
    {
      ...shape,
      meta: { ...metadata },
    },
  ])
}

const isGrouped = (shapes: TLShape[]): boolean => {
  const editor = getEditorObject()
  if (editor == null) return false
  return shapes.length === 1 && editor.isShapeOfType<TLGroupShape>(shapes[0], 'group')
}

const isShapeChildOfGroup = (shape: TLShape): boolean => {
  if (shape == null || shape.type === 'group') return false
  const editor = getEditorObject()
  if (editor == null) return false
  const parent = editor.getShape(shape.parentId)
  return parent != null && editor.isShapeOfType(parent, 'group')
}

export {
  actionShortcuts,
  ActionType,
  addFrame,
  approveShape,
  BACKGROUND_TYPE_TO_PREVIEW_URL_MAPPING,
  BACKGROUND_TYPE_TO_URL_MAPPING,
  BackgroundType,
  calculateIndexKeyForMoveFrame,
  calculateIndexKeyForNewFrame,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  changeAllTldrawDefaultColorStyleColor,
  changeFrameBackground,
  deleteFrame,
  deleteShapeMetadata,
  doesShapeHaveAutoModerationResult,
  embedSizeOptions,
  FRAME_ID_QUERY_PARAM,
  FRAME_PERMISSION_OVERRIDE_MAPPING,
  FramePermissionOverride,
  getCanvasDimensions,
  getFramePermissionOverride,
  getFramePermissions,
  getTailwindBgColorClass,
  getTailwindColorClass,
  getTailwindRingColorClass,
  handleAction,
  isGrouped,
  isShapeApproved,
  isShapeChildOfGroup,
  isShapeCreatedInCurrentTab,
  isShapeSubjectedToAutoModeration,
  isShapeSubjectedToTextModeration,
  isUploadingAttachmentShape,
  MAX_FRAME_LIMIT,
  moveFrame,
  PermissionLevel,
  PERMISSIONS_MAPPING,
  SHAPES_SUBJECTED_TO_AUTO_MODERATION,
  STANDARD_BACKGROUND_TYPES,
  switchFrame,
  TEMPLATE_BACKGROUND_TYPES,
}
export type { EmbedSize, EmbedSizeOption, PageMeta, Permissions }
